import React, { Fragment, useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Helmet } from 'react-helmet';
import Footer from '../../components/footer';
import Navbar from '../../components/Navbar';
import Scrollbar from '../../components/scrollbar';
import Logo from '../../images/Logo/logo-white-bg.png';
import CustomLoader from '../../components/CustomLoader';
import defaultImage from '../../images/barrel.png';
import './style.scss';

const ProductDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(false);
    const [imgError, setImgError] = useState(false);

    useEffect(() => {
        window.scrollTo(0, 0);
        if (id) {
            setLoading(true);
            axios.get(process.env.REACT_APP_API_BASE_URL + `/products/${id}`)
                .then(r => { setProduct(r.data); setLoading(false); })
                .catch(() => setLoading(false));
        }
    }, [id]);

    const handleRequest = () => {
        navigate('/request', { state: { productId: product._id, productName: product.name } });
    };

    const handleKnowMore = () => {
        if (product?.link) window.open(product.link, '_blank', 'noopener,noreferrer');
    };

    return (
        <Fragment>
            <Helmet>
                <title>{product ? `${product.name} — Deep Prabha` : 'Product — Deep Prabha'}</title>
                <meta name="description" content={product?.shortDescription || 'Deep Prabha Transchem'} />
            </Helmet>
            <CustomLoader show={loading} />
            <Navbar topbarBlock="wpo-header-style-3" hclass="wpo-header-style-3" Logo={Logo} />

            {product && (
                <div className="dp-pd">
                    {/* ── HERO STRIP ── */}
                    <div className="dp-pd-hero">
                        <div className="dp-pd-hero__overlay" />
                        <div className="dp-pd-hero__content">
                            <button className="dp-pd-hero__back" onClick={() => navigate('/products')}>
                                ← Back to Products
                            </button>
                            <div className="dp-pd-hero__badge">
                                {product.isActive
                                    ? <span className="dp-pd-hero__badge--active">✓ In Stock</span>
                                    : <span className="dp-pd-hero__badge--inactive">✗ Unavailable</span>}
                            </div>
                            <h1 className="dp-pd-hero__title">{product.name}</h1>
                            <p className="dp-pd-hero__sub">{product.shortDescription}</p>
                            <div className="dp-pd-hero__actions">
                                <button className="dp-pd-btn dp-pd-btn--primary" onClick={handleRequest}>
                                    📩 Request This Product
                                </button>
                                {product.link && (
                                    <button className="dp-pd-btn dp-pd-btn--outline" onClick={handleKnowMore}>
                                        🔗 Know More
                                    </button>
                                )}
                            </div>
                        </div>
                        <div className="dp-pd-hero__wave">
                            <svg viewBox="0 0 1440 70" preserveAspectRatio="none">
                                <path d="M0,35 C360,70 1080,0 1440,35 L1440,70 L0,70 Z" fill="#f4f6fb" />
                            </svg>
                        </div>
                    </div>

                    {/* ── MAIN CONTENT ── */}
                    <div className="dp-pd-body">
                        <div className="dp-pd-layout">

                            {/* ── LEFT: IMAGE CARD ── */}
                            <div className="dp-pd-image-col">
                                <div className="dp-pd-image-card">
                                    <div className="dp-pd-image-card__glow" />
                                    <img
                                        src={imgError || !product.image ? defaultImage : product.image}
                                        alt={product.name}
                                        className="dp-pd-image-card__img"
                                        onError={() => setImgError(true)}
                                    />
                                    <div className="dp-pd-image-card__label">{product.name}</div>
                                </div>

                                {/* Quick info chips */}
                                <div className="dp-pd-chips">
                                    <div className="dp-pd-chip">
                                        <span className="dp-pd-chip__icon">🗓</span>
                                        <div>
                                            <div className="dp-pd-chip__label">Listed</div>
                                            <div className="dp-pd-chip__val">
                                                {new Date(product.createdAt).toLocaleDateString('en-IE', { year: 'numeric', month: 'short', day: 'numeric' })}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="dp-pd-chip">
                                        <span className="dp-pd-chip__icon">🔄</span>
                                        <div>
                                            <div className="dp-pd-chip__label">Updated</div>
                                            <div className="dp-pd-chip__val">
                                                {new Date(product.updatedAt).toLocaleDateString('en-IE', { year: 'numeric', month: 'short', day: 'numeric' })}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="dp-pd-chip dp-pd-chip--full">
                                        <span className="dp-pd-chip__icon">📊</span>
                                        <div>
                                            <div className="dp-pd-chip__label">Availability</div>
                                            <div className="dp-pd-chip__val" style={{ color: product.isActive ? '#10b981' : '#ef4444' }}>
                                                {product.isActive ? 'Available to order' : 'Currently unavailable'}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* ── RIGHT: DETAILS ── */}
                            <div className="dp-pd-info-col">
                                <div className="dp-pd-section-card">
                                    <div className="dp-pd-section-card__header">
                                        <span className="dp-pd-section-card__icon">📋</span>
                                        <h2>Product Overview</h2>
                                    </div>
                                    <p className="dp-pd-section-card__body">{product.longDescription}</p>
                                </div>

                                <div className="dp-pd-section-card dp-pd-section-card--highlight">
                                    <div className="dp-pd-section-card__header">
                                        <span className="dp-pd-section-card__icon">🔬</span>
                                        <h2>Key Properties</h2>
                                    </div>
                                    <div className="dp-pd-props">
                                        <div className="dp-pd-prop">
                                            <span className="dp-pd-prop__key">Product Name</span>
                                            <span className="dp-pd-prop__val">{product.name}</span>
                                        </div>
                                        <div className="dp-pd-prop">
                                            <span className="dp-pd-prop__key">Short Description</span>
                                            <span className="dp-pd-prop__val">{product.shortDescription}</span>
                                        </div>
                                        <div className="dp-pd-prop">
                                            <span className="dp-pd-prop__key">Status</span>
                                            <span className="dp-pd-prop__val" style={{ color: product.isActive ? '#10b981' : '#ef4444', fontWeight: 700 }}>
                                                {product.isActive ? '✓ Active' : '✗ Inactive'}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <div className="dp-pd-cta-card">
                                    <div className="dp-pd-cta-card__left">
                                        <h3>Need this product?</h3>
                                        <p>Submit a request and our team will get back to you within 24 hours with pricing and availability details.</p>
                                    </div>
                                    <button className="dp-pd-btn dp-pd-btn--primary dp-pd-btn--lg" onClick={handleRequest}>
                                        📩 Request This Product →
                                    </button>
                                </div>
                            </div>

                        </div>
                    </div>
                </div>
            )}

            {!loading && !product && (
                <div className="dp-pd-not-found">
                    <div className="dp-pd-not-found__inner">
                        <span>🧪</span>
                        <h2>Product not found</h2>
                        <p>The product you're looking for may have been removed or is unavailable.</p>
                        <button className="dp-pd-btn dp-pd-btn--primary" onClick={() => navigate('/products')}>
                            ← Back to Products
                        </button>
                    </div>
                </div>
            )}

            <Footer />
            <Scrollbar />
        </Fragment>
    );
};

export default ProductDetail;
